import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageSquare, MapPin, Menu, ChevronDown, CreditCard } from "lucide-react";
import { business, nav, navCta, priorityNav, social } from "../data/siteData";
import SocialIcon from "./SocialIcon";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const mobileServiceTabs = [
  { label: "Bathtub Refinishing", to: "/tub-refinishing" },
  { label: "Wall Tile Refinishing", to: "/wall-tile-refinishing" },
  { label: "Countertops", to: "/countertops" },
];

export default function Header() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [stuck, setStuck] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpenMenu(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const isActive = (item) => {
    if (item.to === "/") return location.pathname === "/";
    if (item.to && item.to !== "#" && location.pathname.startsWith(item.to)) return true;
    return (item.children || []).some((c) => c.to && location.pathname === c.to);
  };

  const closeMobile = () => setOpen(false);

  const priorityCls = (active) =>
    `flex items-center gap-1 whitespace-nowrap rounded-sm px-2.5 py-2.5 text-[14px] font-bold uppercase tracking-[0.01em] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#244aa2] xl:px-3 ${
      active ? "text-[#a10000]" : "text-[#242424] hover:text-[#244aa2]"
    }`;

  return (
    <>
    <header className="relative z-50">
      {/* ---------- top utility bar ---------- */}
      <div className="bg-[#244aa2] text-white">
        <div className="mr-container-wide flex h-11 items-center justify-between gap-4">
          <a
            href={`tel:${business.phoneRaw}`}
            data-testid="utility-phone-link"
            className="flex items-center gap-2 text-[15px] font-bold tracking-wide transition-colors hover:text-[#ffd9d9]"
          >
            <Phone className="h-4 w-4" />
            {business.phone}
          </a>
          <div className="flex items-center gap-1">
            <span className="mr-2 hidden text-xs font-semibold uppercase tracking-[0.18em] text-white/80 sm:inline">
              Follow Us
            </span>
            {social.map((s) => (
              <a
                key={s.label}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                title={s.label}
                aria-label={s.label}
                className="flex h-8 w-8 items-center justify-center rounded-sm text-white transition-colors duration-200 hover:bg-white hover:text-[#244aa2]"
              >
                <SocialIcon name={s.icon} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- main white header ---------- */}
      <div
        className={`sticky top-0 z-40 border-b-[3px] border-[#244aa2] bg-white transition-shadow duration-300 ${
          stuck ? "shadow-[0_6px_18px_-8px_rgba(12,42,102,.35)]" : ""
        }`}
      >
        <div className="mr-container-wide flex items-center justify-between gap-4 py-3">
          <Link to="/" title="Connecticut: Mr. Resurface" className="shrink-0" onClick={closeMobile}>
            <img
              src={business.logo}
              alt="Mr. Resurface LLC — Connecticut bathtub reglazing &amp; countertop resurfacing"
              className="h-[50px] w-auto md:h-[56px] xl:h-[60px]"
            />
          </Link>

          {/* ---------- priority links (lg and up) ---------- */}
          <nav
            aria-label="Priority"
            ref={barRef}
            onBlur={(e) => {
              if (!barRef.current?.contains(e.relatedTarget)) setOpenMenu(null);
            }}
            className="hidden flex-1 items-center justify-end lg:flex"
            data-testid="priority-nav"
          >
            <ul className="flex items-center gap-0.5">
              {priorityNav.map((item, idx) => {
                const active = isActive(item);
                const expanded = openMenu === idx;
                return (
                  <li
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => item.children && setOpenMenu(idx)}
                    onMouseLeave={() => item.children && setOpenMenu(null)}
                  >
                    {item.children ? (
                      <button
                        type="button"
                        data-testid={`priority-link-${slug(item.label)}`}
                        aria-haspopup="true"
                        aria-expanded={expanded}
                        onClick={() => setOpenMenu(expanded ? null : idx)}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setOpenMenu(idx);
                          }
                        }}
                        className={priorityCls(active)}
                      >
                        {item.label}
                        <ChevronDown
                          className={`h-3.5 w-3.5 transition-transform duration-200 ${
                            expanded ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    ) : (
                      <Link
                        to={item.to}
                        data-testid={`priority-link-${slug(item.label)}`}
                        className={priorityCls(active)}
                      >
                        {item.label}
                      </Link>
                    )}

                    {item.children && expanded && (
                      <div
                        data-testid={`priority-dropdown-${slug(item.label)}`}
                        className="absolute right-0 top-full z-50 w-[max-content] min-w-[230px] max-w-[min(92vw,320px)] border-t-2 border-[#a10000] bg-[#244aa2] py-1 shadow-xl"
                      >
                        <ul>
                          {item.children.map((c) => (
                            <li key={c.label}>
                              <Link
                                to={c.to}
                                data-testid={`priority-sublink-${slug(c.label)}`}
                                onClick={() => setOpenMenu(null)}
                                className="block px-4 py-2.5 text-[13.5px] font-semibold text-white transition-colors duration-150 hover:bg-white hover:text-[#244aa2] focus-visible:bg-white focus-visible:text-[#244aa2] focus-visible:outline-none"
                              >
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* ---------- payment CTA + menu (all widths) ---------- */}
          <div className="flex shrink-0 items-center gap-2">
            <Link
              to={navCta.to}
              data-testid="nav-make-a-payment-button"
              className="hidden items-center gap-2 whitespace-nowrap bg-[#a10000] px-4 py-2.5 text-[13.5px] font-bold uppercase tracking-[0.04em] text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#7d0000] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#a10000] sm:flex"
            >
              <CreditCard className="h-4 w-4" />
              {navCta.label}
            </Link>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Menu"
                  data-testid="mobile-menu-button"
                  className="flex flex-col items-center gap-0.5 rounded-sm bg-[#244aa2] px-3 py-2 text-white transition-colors hover:bg-[#0c2a66]"
                >
                  <Menu className="h-5 w-5" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">Menu</span>
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                data-testid="mobile-menu-panel"
                className="w-[88vw] max-w-sm overflow-y-auto bg-white p-0"
              >
                <SheetTitle className="sr-only">Main menu</SheetTitle>
                <div className="flex items-center border-b-[3px] border-[#244aa2] px-5 py-4">
                  <img src={business.logo} alt="Mr. Resurface LLC" className="h-12 w-auto" />
                </div>

                <div className="px-4 pt-4">
                  <Link
                    to={navCta.to}
                    onClick={closeMobile}
                    data-testid="mobile-nav-make-a-payment"
                    className="flex items-center justify-center gap-2 bg-[#a10000] px-4 py-3.5 text-sm font-bold uppercase tracking-wide text-white"
                  >
                    <CreditCard className="h-4 w-4" /> Make a Payment
                  </Link>
                </div>

                <ul className="px-2 py-3">
                  {nav.map((item) =>
                    item.children ? (
                      <li key={item.label} className="border-b border-neutral-200">
                        <Collapsible defaultOpen={isActive(item)}>
                          <div className="flex items-center">
                            {item.to !== "#" ? (
                              <Link
                                to={item.to}
                                onClick={closeMobile}
                                data-testid={`mobile-nav-link-${slug(item.label)}`}
                                className="flex-1 px-3 py-3.5 text-sm font-bold uppercase text-[#242424]"
                              >
                                {item.label}
                              </Link>
                            ) : (
                              <CollapsibleTrigger asChild>
                                <button
                                  data-testid={`mobile-nav-link-${slug(item.label)}`}
                                  className="flex-1 px-3 py-3.5 text-left text-sm font-bold uppercase text-[#242424]"
                                >
                                  {item.label}
                                </button>
                              </CollapsibleTrigger>
                            )}
                            <CollapsibleTrigger asChild>
                              <button
                                aria-label={`Toggle ${item.label}`}
                                data-testid={`mobile-nav-toggle-${slug(item.label)}`}
                                className="px-3 py-3.5 text-[#244aa2]"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </button>
                            </CollapsibleTrigger>
                          </div>
                          <CollapsibleContent>
                            <ul className="bg-neutral-50 pb-2">
                              {item.children.map((c) => (
                                <li key={c.label}>
                                  {c.href ? (
                                    <a
                                      href={c.href}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={closeMobile}
                                      className="block px-7 py-3 text-sm font-semibold text-[#244aa2]"
                                    >
                                      {c.label}
                                    </a>
                                  ) : (
                                    <Link
                                      to={c.to}
                                      onClick={closeMobile}
                                      data-testid={`mobile-nav-sublink-${slug(c.label)}`}
                                      className="block px-7 py-3 text-sm font-semibold text-[#244aa2]"
                                    >
                                      {c.label}
                                    </Link>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </CollapsibleContent>
                        </Collapsible>
                      </li>
                    ) : (
                      <li key={item.label} className="border-b border-neutral-200">
                        <Link
                          to={item.to}
                          onClick={closeMobile}
                          data-testid={`mobile-nav-link-${slug(item.label)}`}
                          className="block px-3 py-3.5 text-sm font-bold uppercase text-[#242424]"
                        >
                          {item.label}
                        </Link>
                      </li>
                    )
                  )}
                </ul>

                <div className="space-y-2 px-4 pb-8">
                  <a
                    href={`tel:${business.phoneRaw}`}
                    className="flex items-center justify-center gap-2 bg-[#244aa2] px-4 py-3 text-sm font-bold uppercase text-white"
                  >
                    <Phone className="h-4 w-4" /> {business.phone}
                  </a>
                  <a
                    href={`sms:1${business.phoneDigits}`}
                    className="flex items-center justify-center gap-2 border-2 border-[#244aa2] px-4 py-3 text-sm font-bold uppercase text-[#244aa2]"
                  >
                    <MessageSquare className="h-4 w-4" /> Text Us
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      </header>

      {/* ---------- mobile sticky service tabs ---------- */}
      <nav
        aria-label="Services"
        data-testid="mobile-service-tabs"
        className="sticky top-0 z-30 grid grid-cols-3 border-b border-[#244aa2]/25 bg-white shadow-[0_4px_10px_-8px_rgba(12,42,102,.4)] lg:hidden"
      >
        {mobileServiceTabs.map((t, i) => {
          const active = location.pathname === t.to;
          return (
            <Link
              key={t.to}
              to={t.to}
              onClick={closeMobile}
              data-testid={`mobile-service-tab-${slug(t.label)}`}
              className={`flex min-h-[40px] items-center justify-center px-1 text-center text-[10.5px] font-bold uppercase leading-[1.15] tracking-[0.01em] transition-colors duration-200 ${
                i < mobileServiceTabs.length - 1 ? "border-r border-[#244aa2]/20" : ""
              } ${
                active
                  ? "border-b-2 border-b-[#a10000] text-[#a10000]"
                  : "text-[#244aa2] hover:bg-[#244aa2]/5"
              }`}
            >
              {t.label}
            </Link>
          );
        })}
      </nav>

      {/* ---------- mobile call / text / directions strip ---------- */}
      <div className="grid grid-cols-3 border-b border-[#0c2a66]/20 bg-[#0c2a66] text-white xl:hidden">
        <a
          href={`tel:${business.phoneRaw}`}
          className="flex items-center justify-center gap-1.5 border-r border-white/20 py-2.5 text-[12px] font-bold"
        >
          <Phone className="h-3.5 w-3.5" /> Call
        </a>
        <a
          href={`sms:1${business.phoneDigits}`}
          className="flex items-center justify-center gap-1.5 border-r border-white/20 py-2.5 text-[12px] font-bold"
        >
          <MessageSquare className="h-3.5 w-3.5" /> Text Us
        </a>
        <a
          href={business.directionsUrl}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center gap-1.5 py-2.5 text-[12px] font-bold"
        >
          <MapPin className="h-3.5 w-3.5" /> Directions
        </a>
      </div>
    </>
  );
}
